<template>
  <div>
    <section class="content-header">
      <h1>Top Gains</h1>
    </section>

    <section class="content">
      <!-- Stats overview -->
      <div class="cyber-stats-grid">
        <div class="cyber-stats-grid__card">
          <div class="cyber-stats-grid__glow"></div>
          <div class="cyber-stats-grid__value"><span class="count">{{ this.stats.direct }}</span></div>
          <div class="cyber-stats-grid__unit">USD</div>
          <div class="cyber-stats-grid__divider"></div>
          <div class="cyber-stats-grid__label">Total Direct</div>
        </div>

        <div class="cyber-stats-grid__card">
          <div class="cyber-stats-grid__glow cyber-stats-grid__glow--cyan"></div>
          <div class="cyber-stats-grid__value"><span class="count">{{ this.stats.incentive }}</span></div>
          <div class="cyber-stats-grid__unit">USD</div>
          <div class="cyber-stats-grid__divider"></div>
          <div class="cyber-stats-grid__label">Total Passive</div>
        </div>

        <div class="cyber-stats-grid__card">
          <div class="cyber-stats-grid__glow cyber-stats-grid__glow--green"></div>
          <div class="cyber-stats-grid__value"><span class="count">{{ this.stats.binary }}</span></div>
          <div class="cyber-stats-grid__unit">USD</div>
          <div class="cyber-stats-grid__divider"></div>
          <div class="cyber-stats-grid__label">Total Binary</div>
        </div>

        <div class="cyber-stats-grid__card">
          <div class="cyber-stats-grid__glow cyber-stats-grid__glow--magenta"></div>
          <div class="cyber-stats-grid__value"><span class="count">{{ this.stats.residual }}</span></div>
          <div class="cyber-stats-grid__unit">USD</div>
          <div class="cyber-stats-grid__divider"></div>
          <div class="cyber-stats-grid__label">Total Residual</div>
        </div>

        <div class="cyber-stats-grid__card cyber-stats-grid__card--wide">
          <div class="cyber-stats-grid__glow"></div>
          <div class="cyber-stats-grid__value"><span class="count">{{ this.stats.career }}</span></div>
          <div class="cyber-stats-grid__unit">USD</div>
          <div class="cyber-stats-grid__divider"></div>
          <div class="cyber-stats-grid__label">Total Career</div>
        </div>
      </div>

      <!-- Profit table -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="cyber-table-box">
            <div class="cyber-table-box__glow"></div>
            <div class="cyber-table-box__header">
              <div class="d-flex align-items-center">
                <i class="mdi mdi-chart-timeline-variant mr-2" style="color: #B09A4E;"></i>
                <h4>Profit Details</h4>
              </div>
            </div>
            <div class="cyber-table-box__body">
              <div class="table-responsive">
                <table class="table table-bordered no-margin">
                  <thead>
                    <tr>
                      <th class="text-left">ID</th>
                      <th class="text-center">Value</th>
                      <th class="text-center">Type</th>
                      <th class="text-center">Status</th>
                      <th class="text-right">Through</th>
                      <th class="text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="reward in tableData" :key="reward.id">
                      <td class="text-left">{{ reward.id }}</td>
                      <td>
                        <span class="cyber-value">{{ reward.value }} {{ reward.currency.name }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-dark">
                          {{ reward.reward_type === "incentive" ? "daily" : reward.reward_type }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          class="badge"
                          :class="{
                            'badge-warning': reward.reward_status === 'pending',
                            'badge-success': reward.reward_status === 'paid',
                            'badge-danger': reward.reward_status === 'cancelled'
                          }"
                        >
                          {{ reward.reward_status === "pending" ? "Unconfirmed" :
                             reward.reward_status === "paid" ? "Confirmed" : "Canceled" }}
                        </span>
                      </td>
                      <td class="text-right">
                        <span class="badge badge-dark" v-if="reward.referred_user">{{ reward.referred_user }}</span>
                        <span class="badge badge-dark" v-if="reward.reward_type === 'binary' && reward.redeemed_binary_points > 0">
                          {{ reward.redeemed_binary_points }} Binary Points
                        </span>
                        <span class="badge badge-dark" v-if="reward.reward_type === 'incentive'">
                          {{ reward.subscription.plan.name }}
                        </span>
                      </td>
                      <td class="text-right">
                        <time class="cyber-date">{{ reward.created_at | formatDate("MMMM Do YYYY hh:mm A") }}</time>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.cyber-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 10px;
}

.cyber-stats-grid__card {
  background: linear-gradient(135deg, #252525, #2e2e2e);
  border: 1px solid #3d3d37;
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.cyber-stats-grid__card:hover {
  border-color: rgba(176, 154, 78, 0.2);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.cyber-stats-grid__glow {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #B09A4E, transparent);
}

.cyber-stats-grid__glow--cyan {
  background: linear-gradient(90deg, transparent, #7BBFB5, transparent);
}

.cyber-stats-grid__glow--green {
  background: linear-gradient(90deg, transparent, #7BC88F, transparent);
}

.cyber-stats-grid__glow--magenta {
  background: linear-gradient(90deg, transparent, #C46B8F, transparent);
}

.cyber-stats-grid__value {
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #E0DFD8;
}

.cyber-stats-grid__unit {
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.75rem;
  color: #9A998F;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.cyber-stats-grid__divider {
  width: 30px;
  height: 2px;
  background: #B09A4E;
  margin: 10px auto;
  box-shadow: 0 0 6px rgba(176, 154, 78, 0.3);
}

.cyber-stats-grid__label {
  font-family: 'Orbitron', sans-serif;
  font-size: 0.65rem;
  color: #9A998F;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Table box */
.cyber-table-box {
  background: #252525;
  border: 1px solid #3d3d37;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
}

.cyber-table-box__glow {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(176, 154, 78, 0.3), transparent);
}

.cyber-table-box__header {
  padding: 18px 20px;
  border-bottom: 1px solid #3d3d37;
}

.cyber-table-box__header h4 {
  font-family: 'Orbitron', sans-serif !important;
  font-size: 0.8rem !important;
  color: #E0DFD8 !important;
  margin: 0 !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cyber-table-box__body {
  padding: 0;
}

.cyber-value {
  font-weight: 600;
  color: #E0DFD8;
}

.cyber-date {
  font-size: 0.8rem;
  color: #9A998F;
}

@media (max-width: 767px) {
  .cyber-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .cyber-stats-grid__value {
    font-size: 1.1rem;
  }
}
</style>

<script>
let $ = window.$;
import { formatDollar, formatDate } from "@/filters";

export default {
  name: "reports",
  props: {
    title: String
  },
  filters: { formatDollar, formatDate },
  data() {
    return {
      type: "",
      loading: false,
      tableData: [],
      stats: {
        incentive: 0.0,
        residual: 0.0,
        direct: 0.0,
        binary: 0.0,
        career: 0.0,
        market_club: 0.0
      },
      per_page: 25,
      page: 1,
      counter: 0,
      noResults: false
    };
  },
  watch: {
    counter: function(newValue) {
      if (newValue === 4) this.animation();
    }
  },
  mounted() {
    this.scroll();
  },
  created() {
    this.last_subs = this.$store.getters.currentUser.last_subs;
    this.getData(
      "incentive,binary,direct,residual,market_club,career",
      false,
      this.per_page,
      this.page
    );
    this.getData("market_club", true);
    this.getData("incentive", true);
    this.getData("direct", true);
    this.getData("binary", true);
    this.getData("residual", true);
    this.getData("career", true);
  },
  methods: {
    scroll() {
      window.onscroll = () => {
        let bottomOfWindow =
          Math.max(
            window.pageYOffset,
            document.documentElement.scrollTop,
            document.body.scrollTop
          ) +
            window.innerHeight ===
          document.documentElement.offsetHeight;

        if (bottomOfWindow && !this.noResults) {
          this.getData(
            "incentive,binary,direct,residual,market_club,career",
            false,
            this.per_page,
            this.page + 1
          );
          this.page += 1;
        }
      };
    },
    animation() {
      setTimeout(() => {
        $(".count").each(function() {
          $(this)
            .prop("Counter", 0)
            .animate(
              { Counter: $(this).text() },
              {
                duration: 4000,
                easing: "swing",
                step: function(now) {
                  $(this).text(parseFloat(now).toFixed(2));
                }
              }
            );
        });
      }, 500);
    },
    getData(type, total, per_page, page) {
      this.loading = true;
      this.$http({
        url:
          "/api/v1/client/rewards?type=" + type +
          "&per_page=" + per_page +
          "&page=" + page +
          "&total=" + total,
        method: "get",
        headers: {
          "content-type": "application/json",
          Authorization: this.$store.getters.authToken
        }
      })
        .then((response) => {
          this.loading = false;
          if (total) {
            this.stats[type] = parseFloat(response.data);
            this.counter += 1;
          } else {
            response.data.map((item) => {
              this.tableData.push(item);
            });
            if (response.data.length === 0) this.noResults = true;
          }
        })
        .catch((error) => {
          error.response.data &&
            error.response.data.map((m) => {
              this.$toasted.show(m, {
                position: "top-right",
                duration: 5000,
                type: "error",
                closeOnSwipe: true
              });
            });
          this.loading = false;
        });
    }
  }
};
</script>
